import i18n from '@/utils/i18n';
import React, { useEffect, useMemo } from 'react';
import { ApplicationType } from '@/contexts/dynamic-config';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, LAYOUT_MAX_WIDTH, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { formatSummaryDate } from '@/utils/date';
import { FULFILLMENT_SHIPPING_TYPES } from '@domain/fulfillment-settings/blocs/order-availability';
import {
  FulfillmentName,
  SmartOrder_PlacedSmartOrderQuery,
  SmartOrder_PlacedSmartOrderQueryVariables,
} from '@/types';
import { getPlacedOrderCopy } from '@/utils/order-submitted';
import { HappyBox } from '@/components/happy-box';
import { Invoice } from '@/components/invoice';
import { MultiStyleText } from '@/components/multi-style-text';
import { OrderFee } from '@/utils/money';
import { OrderStatus } from '@/enums';
import { ORIGINAL_INBOUND_SIDEBAR_MAX_WIDTH } from '@/screens/order-inbounds/utils';
import { partnerSupplier } from '@/utils/environment';
import { PLACED_SMART_ORDER_QUERY } from '@/contexts/smart-order-queries';
import { ScrollView, Text, View } from 'react-native';
import { SMART_ORDER_CONTAINER_ROUTES } from '@/navigators/routes';
import { SmartOrderContainerStackParams } from '@/navigators/types';
import { StackScreenProps } from '@react-navigation/stack';
import { useGetOrderForRestaurant } from '@/components/orders/hooks/use-get-order';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useSmartOrderAuthenticatedUserContext } from '@/contexts/user-context';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';
import { useUniversalAppLink } from '@/hooks/use-universal-app-link';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export type ScreenProps = StackScreenProps<
  SmartOrderContainerStackParams,
  SMART_ORDER_CONTAINER_ROUTES.PlacedOrder
>;

export const SmartOrderSubmittedScreen: React.FC<ScreenProps> = ({ route }) => {
  const [styles] = useStyles();
  const { showToast } = useToasts();
  const {
    orderUUID,
    selectedRestaurantUUID,
    selectedRestaurantRelationship: {
      chatUUIDs,
      supplierUUID,
      displayItemPrices,
      name: restaurantName,
    },
  } = useSmartOrderAuthenticatedUserContext();
  const { useV2Invoices } = usePaymentsContext();

  const placedOrderUUID = route.params?.orderUUID
    ? route.params.orderUUID
    : orderUUID;
  const submittedOrderProps = route.params?.submittedOrderProps;

  const { data, hasResponse } = useWrappedQuery<
    SmartOrder_PlacedSmartOrderQuery,
    SmartOrder_PlacedSmartOrderQueryVariables
  >(PLACED_SMART_ORDER_QUERY, {
    skip: !placedOrderUUID,
    variables: {
      orderUUID: placedOrderUUID!,
    },
  });

  const { data: orderData } = useGetOrderForRestaurant(
    placedOrderUUID!,
    chatUUIDs[0],
    selectedRestaurantUUID,
    supplierUUID,
  );
  const { hasResponse: appLinkHasResponse, onPressLink } =
    useUniversalAppLink();
  const isOrderAgentOnly =
    useSupplierDynamicConfigValue('application_type') ===
    ApplicationType.ORDER_AGENT_ONLY;

  const order = orderData?.getOrder.order;
  const orderInvoice = orderData?.getOrderInvoiceByOrder?.order_invoice;
  const orderFees: OrderFee[] = useMemo(() => {
    return orderData?.getOrderFees || [];
  }, [orderData]);

  const splitOrdersCount = data?.orders_aggregate.aggregate?.count;
  const desiredFulfillmentTime = order?.restaurant_desired_delivery_time;
  // TODO: https://usepepper.atlassian.net/browse/EXP-1704
  const fulfillmentDates =
    submittedOrderProps?.fulfillmentDates ||
    (desiredFulfillmentTime
      ? [formatSummaryDate(new Date(desiredFulfillmentTime))]
      : []);
  const externalReviewRequired =
    submittedOrderProps?.externalReviewRequired ||
    order?.status === OrderStatus.InExternalReview;
  const reviewRequired =
    submittedOrderProps?.reviewRequired ||
    order?.status === OrderStatus.InReview;
  const isSplitOrder =
    submittedOrderProps?.isSplitOrder || splitOrdersCount! > 0;
  const isShippingOrder =
    submittedOrderProps?.isShippingOrder ||
    FULFILLMENT_SHIPPING_TYPES.includes(
      order?.fulfillment_type as FulfillmentName,
    );

  useEffect(() => {
    if (submittedOrderProps?.completionToast) {
      showToast(submittedOrderProps?.completionToast);
    }
  }, [submittedOrderProps, showToast]);

  const { loyaltyEarnedMessage, orderFulfillmentMessage, orderTitle } =
    getPlacedOrderCopy({
      externalReviewRequired,
      fulfillmentDates,
      isShippingOrder,
      isSplitOrder,
      loyaltyEarns: submittedOrderProps?.loyaltyEarns ?? [],
      reviewRequired,
      smartOrderRestaurantName: restaurantName,
    });

  return (
    <ScrollView contentContainerStyle={styles.base}>
      <HappyBox />
      <Text style={[TYPOGRAPHY.h3, styles.titleText]}>{orderTitle}</Text>
      {hasResponse ? (
        <Text style={[TYPOGRAPHY.actionSheetMessageText, styles.messageText]}>
          {orderFulfillmentMessage}
        </Text>
      ) : null}
      {loyaltyEarnedMessage ? (
        <MultiStyleText
          defaultStyle={[
            TYPOGRAPHY.actionSheetMessageText,
            styles.loyaltyMessage,
          ]}
          emphasisStyle={FONTS.primaryBold}
        >
          {loyaltyEarnedMessage}
        </MultiStyleText>
      ) : null}
      {appLinkHasResponse && !isOrderAgentOnly ? (
        <View style={styles.downloadContainer}>
          <Text style={[TYPOGRAPHY.large, styles.downloadTitle]}>
            {i18n.t('order_placed.download_message', {
              appName: partnerSupplier.fullName,
            })}
          </Text>
          <Button
            onPress={onPressLink}
            style={styles.button}
            trackingData={{ name: 'smart_order_placed.download_app_button' }}
            type="primary"
          >
            {i18n.t('order_placed.download_app')}
          </Button>
        </View>
      ) : null}
      {order ? (
        <View style={styles.itemsContainer}>
          <Invoice
            displayItemPrices={displayItemPrices}
            isV2Invoice={useV2Invoices}
            order={order}
            orderFees={orderFees}
            orderInvoice={orderInvoice}
          />
        </View>
      ) : null}
    </ScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    marginHorizontal: 'auto',
    maxWidth: LAYOUT_MAX_WIDTH,
    padding: SCREEN_PADDING,
    width: '100%',
  },
  button: {
    backgroundColor: theme.colors.accent,
    borderColor: theme.colors.accent,
  },
  downloadContainer: {
    backgroundColor: theme.colors.background,
    borderRadius: 8,
    marginTop: 24,
    maxWidth: ORIGINAL_INBOUND_SIDEBAR_MAX_WIDTH,
    padding: 16,
  },
  downloadTitle: {
    ...FONTS.primaryBold,
    marginBottom: 16,
    textAlign: 'center',
  },
  itemsContainer: {
    marginTop: 24,
    width: '100%',
  },
  loyaltyMessage: {
    marginTop: theme.sk.spacing.spaceSmall,
    textAlign: 'center',
  },
  messageText: {
    textAlign: 'center',
  },
  titleText: {
    marginBottom: 8,
    textAlign: 'center',
  },
}));
